import loadScript from 'load-script';

import { SDK_GLOBAL } from './constants';
import {
	MATCH_END_QUERY,
	MATCH_NUMERIC,
	MATCH_PLAYLIST,
	MATCH_START_QUERY,
	MATCH_START_STAMP,
	MATCH_URL_YOUTUBE,
	MATCH_USER_UPLOADS,
} from './constants';
import { YouTubePlayer, YouTubePlayerEvent, YouTubePlayerEventListeners } from './types';
import { GlobalSDK, VideoUrl } from './types';

// Parse YouTube URL for a start time param, ie ?t=1h14m30s
// and return the start time in seconds
const parseTimeParam = (url: VideoUrl, pattern: RegExp) => {
	if (Array.isArray(url)) {
		return undefined;
	}
	const match = url.match(pattern);
	if (match) {
		const stamp = match[1];
		if (stamp.match(MATCH_START_STAMP)) {
			return parseTimeString(stamp);
		}
		if (MATCH_NUMERIC.test(stamp)) {
			return parseInt(stamp);
		}
	}
	return undefined;
};

const parseTimeString = (stamp: string) => {
	let seconds = 0;
	let array = MATCH_START_STAMP.exec(stamp);
	while (array !== null) {
		const [, count, period] = array;
		if (period === 'h') seconds += parseInt(count, 10) * 60 * 60;
		if (period === 'm') seconds += parseInt(count, 10) * 60;
		if (period === 's') seconds += parseInt(count, 10);
		array = MATCH_START_STAMP.exec(stamp);
	}
	return seconds;
};

export const parseStartTime = (url: VideoUrl) => {
	return parseTimeParam(url, MATCH_START_QUERY);
};

export const parseEndTime = (url: VideoUrl) => {
	return parseTimeParam(url, MATCH_END_QUERY);
};

const getGlobal = (key: GlobalSDK) => {
	if (window[key]) {
		return window[key];
	}
	if (window?.exports?.[key]) {
		return window.exports[key];
	}
	if (window?.module?.exports?.[key]) {
		return window.module.exports[key];
	}
	return null;
};

// Util function to load an external SDK
// or return the SDK if it is already loaded
export const getSDK = (url: string, sdkGlobal: GlobalSDK) => {
	const existingGlobal = getGlobal(sdkGlobal);

	if (existingGlobal) {
		return existingGlobal;
	}

	loadScript(url, (err) => {
		if (!err) {
			return new Promise((resolve) => {
				resolve(getGlobal(sdkGlobal));
			});
		}
	});
};

export const handleErrorDefault = (errorCode: string, url: VideoUrl) => {
	if (errorCode === '150') {
		// eslint-disable-next-line no-console
		console.error(
			`Error: ${errorCode}. There's been a problem embedding the video ${
				Array.isArray(url) ? 'playlist ' + `${url[0]}..` : url
			}. The video might be restricted.`,
		);
	} else {
		// eslint-disable-next-line no-console
		console.error(
			`Error: ${errorCode}. There's been a problem embedding the video ${
				Array.isArray(url) ? 'playlist ' + `${url[0]}..` : url
			}.`,
		);
	}
};

export const getID = (url: VideoUrl) => {
	if (!url || Array.isArray(url) || MATCH_PLAYLIST.test(url)) {
		return null;
	}
	return url.match(MATCH_URL_YOUTUBE)?.[1];
};

export const parsePlaylist = (url: VideoUrl) => {
	if (Array.isArray(url)) {
		return {
			listType: 'playlist',
			playlist: url.map(getID).join(','),
		};
	}
	if (MATCH_PLAYLIST.test(url)) {
		const playlistId = url.match(MATCH_PLAYLIST)?.[1];

		return {
			listType: 'playlist',
			list: playlistId?.replace(/^UC/, 'UU'),
		};
	}
	if (MATCH_USER_UPLOADS.test(url)) {
		const username = url.match(MATCH_USER_UPLOADS)?.[1];

		return {
			listType: 'user_uploads',
			list: username,
		};
	}
	return null;
};

const seekTo = (
	currentPlayer: YouTubePlayer,
	autoplay: boolean,
	amount: number,
	keepPlaying = false,
) => {
	if (currentPlayer) {
		currentPlayer.seekTo(amount);
	}
	if (!keepPlaying && !autoplay) {
		currentPlayer.pauseVideo();
	}
};

export const onStateChange = (
	event: YouTubePlayerEvent,
	currentPlayer: YouTubePlayer,
	eventListeners: YouTubePlayerEventListeners,
	playerProps: { loop: boolean; autoplay: boolean; start?: number },
) => {
	const { data } = event;
	const { UNSTARTED, PLAYING, PAUSED, BUFFERING, ENDED, CUED } = window[SDK_GLOBAL].PlayerState;
	const { onUnstarted, onPlay, onPause, onBuffer, onReady, onEnded, onBufferEnd } = eventListeners;
	const { loop, autoplay, start } = playerProps;

	if (currentPlayer === null) return;

	const canGetPlaylist = !!currentPlayer.getPlaylist();

	switch (data) {
		case UNSTARTED:
			onUnstarted?.();
			break;
		case PLAYING:
			onPlay?.(currentPlayer, event);
			onBufferEnd?.(currentPlayer);
			break;
		case PAUSED:
			onPause?.(currentPlayer);
			break;
		case BUFFERING:
			onBuffer?.(currentPlayer);
			break;
		case CUED:
			onReady?.(currentPlayer);
			break;
		case ENDED:
			// Only loop manually if not playing a playlist
			if (loop && !canGetPlaylist) {
				if (start) {
					seekTo(currentPlayer, autoplay, start);
				} else {
					currentPlayer.playVideo();
				}
			}
			onEnded?.(currentPlayer);
			break;
		default:
			break;
	}
};
